import React, { useEffect, useRef, useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import UrduKeyboard from '../../Components/UrduKeyboard';

function CustomerKhata({
  company,
  companyLanguage,
  ledgerParties,
  customerSearchFilter,
  setCustomerSearchFilter,
  showCustomerForm,
  setShowCustomerForm,
  ledgerPartyForm,
  handleLedgerPartyFormChange,
  handleCreateLedgerParty,
  editingPartyId,
  setEditingPartyId,
  isLoadingLedger,
  selectedLedgerParty,
  fetchLedgerTransactions,
  handleEditLedgerParty,
  handleDeleteLedgerParty,
  ledgerTransactions,
  ledgerTransactionForm,
  handleLedgerTransactionFormChange,
  handleCreateLedgerTransaction,
  activeTab,
  activeLedgerTab,
  setLedgerPartyForm,
  sales = []
}) {
  const formRef = useRef(null);
  const printRef = useRef(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showUrduKeyboard, setShowUrduKeyboard] = useState(false);
  const [showSearchUrduKeyboard, setShowSearchUrduKeyboard] = useState(false);
  const urduNameInputRef = useRef(null);
  const searchInputRef = useRef(null);

  // Handle Urdu keyboard input for name field
  const handleUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setLedgerPartyForm((prev) => ({
        ...prev,
        nameUrdu: prev.nameUrdu ? prev.nameUrdu.slice(0, -1) : ''
      }));
    } else {
      setLedgerPartyForm((prev) => ({
        ...prev,
        nameUrdu: (prev.nameUrdu || '') + key
      }));
    }
  };

  // Handle Urdu keyboard input for search field
  const handleSearchUrduKeyPress = (key) => {
    if (key === 'backspace') {
      setCustomerSearchFilter((prev) => prev ? prev.slice(0, -1) : '');
    } else {
      setCustomerSearchFilter((prev) => (prev || '') + key);
    }
  };

  // Toggle Urdu keyboard on search input click
  const handleSearchInputClick = () => {
    setShowSearchUrduKeyboard(prev => !prev);
  };

  // Helpers to show supplier + vehicle (gardi) in khata rows
  const findSaleById = (saleId) => {
    if (!saleId) return null;
    const sid = saleId?._id ? saleId._id.toString() : saleId.toString();
    return sales.find(s => (s?._id || '').toString() === sid) || null;
  };

  const getSupplierAndVehicleForTx = (tx) => {
    // Prefer Sale receipt data (most accurate for supplier + gardi)
    const sale = (tx?.saleData && typeof tx.saleData === 'object')
      ? tx.saleData
      : (tx?.saleId ? findSaleById(tx.saleId) : null);

    const supplierName = sale?.supplierNameUrdu || sale?.supplierName || '-';
    const gardiNumber = sale?.supplierVehicleNumber
      || tx?.billId?.expenses?.ActualVehicle
      || tx?.billId?.expenses?.Vehicle
      || '-';

    return { supplierName, gardiNumber };
  };

  // Auto-scroll to form when it opens
  useEffect(() => {
    if (showCustomerForm && formRef.current) {
      // Small delay to ensure form is rendered
      setTimeout(() => {
        formRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
        // Also focus on first input field
        const firstInput = formRef.current?.querySelector('input[name="name"]');
        if (firstInput) {
          setTimeout(() => {
            firstInput.focus();
          }, 300);
        }
      }, 100);
    }
  }, [showCustomerForm]);

  // Filter transactions by date
  const filterTransactionsByDate = (transactions) => {
    if (!dateFrom && !dateTo) return transactions;
    
    return transactions.filter(tx => {
      const txDate = new Date(tx.date || tx.createdAt || 0);
      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo) : null;
      
      if (fromDate && toDate) {
        fromDate.setHours(0, 0, 0, 0);
        toDate.setHours(23, 59, 59, 999);
        return txDate >= fromDate && txDate <= toDate;
      } else if (fromDate) {
        fromDate.setHours(0, 0, 0, 0);
        return txDate >= fromDate;
      } else if (toDate) {
        toDate.setHours(23, 59, 59, 999);
        return txDate <= toDate;
      }
      return true;
    });
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    if (!printRef.current) {
      toast.error(companyLanguage === 'ur' ? 'مواد نہیں ملا' : 'Content not found');
      return;
    }

    try {
      setIsGeneratingPDF(true);
      toast.info(companyLanguage === 'ur' ? 'PDF بنایا جا رہا ہے...' : 'Generating PDF...');

      const element = printRef.current;

      // Show print header and hide elements that should be hidden in PDF
      const printHeader = element.querySelector('[class*="print:block"]');
      const hiddenElements = element.querySelectorAll('[class*="print:hidden"]');
      
      // Show print header
      if (printHeader) {
        printHeader.style.display = 'block';
      }
      
      // Hide elements that should be hidden in print/PDF
      hiddenElements.forEach(el => {
        el.style.display = 'none';
      });

      // Add temporary styles for PDF
      const style = document.createElement('style');
      style.id = 'pdf-print-styles';
      style.textContent = `
        .bg-gradient-to-br { background: white !important; }
        .from-green-50 { background: white !important; }
        .via-emerald-50 { background: white !important; }
        .to-teal-50 { background: white !important; }
        .bg-white { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0.5cm !important; }
        .rounded-lg { border-radius: 0 !important; }
        .shadow-lg { box-shadow: none !important; }
        table { width: 100% !important; max-width: 100% !important; table-layout: auto !important; }
        .overflow-x-auto { width: 100% !important; overflow: visible !important; }
        div[ref] { width: 100% !important; max-width: 100% !important; margin: 0 !important; padding: 0 !important; }
      `;
      document.head.appendChild(style);

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });

      // Restore original display states
      if (printHeader) {
        printHeader.style.display = '';
      }
      hiddenElements.forEach(el => {
        el.style.display = '';
      });

      // Remove temporary styles
      const tempStyle = document.getElementById('pdf-print-styles');
      if (tempStyle) {
        document.head.removeChild(tempStyle);
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const partyName = selectedLedgerParty?.name || 'Customer';
      const dateRange = dateFrom && dateTo 
        ? `_${dateFrom}_to_${dateTo}` 
        : dateFrom 
        ? `_from_${dateFrom}` 
        : dateTo 
        ? `_to_${dateTo}` 
        : '';
      const fileName = `Customer_Khata_${partyName}${dateRange}_${new Date().toISOString().split('T')[0]}.pdf`;
      
      pdf.save(fileName);
      toast.success(companyLanguage === 'ur' ? 'PDF ڈاؤن لوڈ ہو گیا!' : 'PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error(companyLanguage === 'ur' ? 'PDF بنانے میں ناکام' : 'Failed to generate PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Download Excel
  const handleDownloadExcel = () => {
    if (!selectedLedgerParty) {
      toast.error(companyLanguage === 'ur' ? 'کوئی پارٹی منتخب نہیں' : 'No party selected');
      return;
    }

    try {
      // Get all transactions (same logic as display)
      const customerSales = selectedLedgerParty 
        ? sales.filter(sale => sale.customerPartyId === selectedLedgerParty._id)
        : [];
      
      const salesWithLedgerTransactions = new Set();
      ledgerTransactions.forEach(tx => {
        const saleId = tx.saleId?._id || (tx.saleId && typeof tx.saleId === 'object' ? tx.saleId._id : tx.saleId) || tx.saleId;
        if (saleId) {
          salesWithLedgerTransactions.add(saleId.toString());
        }
      });
      
      const saleTransactions = customerSales
        .filter(sale => !salesWithLedgerTransactions.has(sale._id.toString()))
        .map(sale => {
          const saleAmount = parseFloat(sale.totalAmount || 0);
          const amountPaid = parseFloat(sale.amountPaid || 0);
          const saleSerial = sale.serialNumber || sale._id?.toString().slice(-6) || '';
          
          return {
            _id: `sale_${sale._id}`,
            date: sale.date || sale.createdAt,
            description: `سیل #${saleSerial}`,
            voucherNumber: saleSerial,
            sale: saleAmount,
            recovery: amountPaid,
            balanceAfter: null,
            saleId: sale._id,
            isFromSale: true
          };
        });
      
      const allTransactions = [...ledgerTransactions, ...saleTransactions];
      const filteredTransactions = filterTransactionsByDate(allTransactions);
      
      // Process transactions similar to display logic
      const groupedBySale = {};
      const separateTransactions = [];
      
      filteredTransactions.forEach(tx => {
        let saleIdValue = null;
        if (tx.saleId) {
          if (typeof tx.saleId === 'object' && tx.saleId._id) {
            saleIdValue = tx.saleId._id.toString();
          } else if (typeof tx.saleId === 'object') {
            saleIdValue = tx.saleId.toString();
          } else {
            saleIdValue = tx.saleId.toString();
          }
        }
        
        if (saleIdValue && (tx.type === 'debit' || tx.type === 'credit' || tx.isFromSale)) {
          if (!groupedBySale[saleIdValue]) {
            groupedBySale[saleIdValue] = {
              transactions: [],
              saleId: saleIdValue
            };
          }
          groupedBySale[saleIdValue].transactions.push(tx);
        } else {
          separateTransactions.push(tx);
        }
      });
      
      const excelData = [];
      
      // Add header
      excelData.push([
        companyLanguage === 'ur' ? 'تاریخ' : 'Date',
        companyLanguage === 'ur' ? 'تفصیل' : 'Particulars',
        companyLanguage === 'ur' ? 'سپلائر' : 'Supplier',
        companyLanguage === 'ur' ? 'گاڑی نمبر' : 'Gardi No.',
        companyLanguage === 'ur' ? 'واؤچر نمبر' : 'Vch No.',
        companyLanguage === 'ur' ? 'سیل' : 'Sale',
        companyLanguage === 'ur' ? 'ریکوری' : 'Recovery',
        companyLanguage === 'ur' ? 'بیلنس' : 'Balance'
      ]);
      
      // Add opening balance
      excelData.push([
        '',
        companyLanguage === 'ur' ? 'ابتدائی بیلنس' : 'Opening Balance',
        '-',
        '-',
        '-',
        '-',
        '-',
        selectedLedgerParty.openingBalance || 0
      ]);
      
      // Process grouped transactions
      Object.values(groupedBySale).forEach(group => {
        const saleTxs = group.transactions;
        let saleAmount = 0;
        let recoveryAmount = 0;
        let date = saleTxs[0].date;
        let voucherNumber = '-';
        // Resolve supplier + gardi from sale receipt (by saleId)
        const saleObj = findSaleById(group.saleId);
        const supplierName = saleObj?.supplierNameUrdu || saleObj?.supplierName || '-';
        const gardiNumber = saleObj?.supplierVehicleNumber || '-';
        
        // Avoid double-counting if both synthetic (from Sale model) and real ledger tx exist
        const hasRealDebit = saleTxs.some(t => !t.isFromSale && t.type === 'debit');
        const hasRealCredit = saleTxs.some(t => !t.isFromSale && t.type === 'credit');

        saleTxs.forEach(tx => {
          if (tx.isFromSale) {
            // Only use synthetic values when real ledger values are missing
            if (!hasRealDebit) saleAmount += tx.sale || 0;
            if (!hasRealCredit) recoveryAmount += tx.recovery || 0;
            voucherNumber = tx.voucherNumber || '-';
          } else {
            if (tx.type === 'debit') {
              saleAmount += tx.amount || 0;
            } else if (tx.type === 'credit') {
              recoveryAmount += tx.amount || 0;
            }
          }
        });
        
        excelData.push([
          new Date(date).toLocaleDateString(),
          `سیل #${voucherNumber}`,
          supplierName,
          gardiNumber,
          voucherNumber,
          saleAmount,
          recoveryAmount,
          ''
        ]);
      });
      
      // Process separate transactions
      separateTransactions.forEach(tx => {
        let voucherNumber = '-';
        if (tx.billId) {
          const billIdValue = tx.billId?._id || (tx.billId && typeof tx.billId === 'object' ? tx.billId._id : tx.billId) || tx.billId;
          voucherNumber = billIdValue ? billIdValue.toString().slice(-6) : '-';
        }
        
        const { supplierName, gardiNumber } = getSupplierAndVehicleForTx(tx);
        excelData.push([
          new Date(tx.date).toLocaleDateString(),
          tx.description || '-',
          supplierName,
          gardiNumber,
          voucherNumber,
          tx.type === 'debit' ? (tx.amount || 0) : '',
          tx.type === 'credit' ? (tx.amount || 0) : '',
          tx.balanceAfter || ''
        ]);
      });
      
      // Create workbook and worksheet
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customer Khata');
      
      // Generate filename
      const partyName = selectedLedgerParty.name || 'Customer';
      const dateRange = dateFrom && dateTo 
        ? `_${dateFrom}_to_${dateTo}` 
        : dateFrom 
        ? `_from_${dateFrom}` 
        : dateTo 
        ? `_to_${dateTo}` 
        : '';
      const fileName = `Customer_Khata_${partyName}${dateRange}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      XLSX.writeFile(wb, fileName);
      toast.success(companyLanguage === 'ur' ? 'Excel فائل ڈاؤن لوڈ ہو گئی' : 'Excel file downloaded successfully');
    } catch (error) {
      console.error('Excel download error:', error);
      toast.error(companyLanguage === 'ur' ? 'Excel ڈاؤن لوڈ میں ناکام' : 'Failed to download Excel');
    }
  };

  // Print
  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      {/* PDF/Print Container */}
      <div ref={printRef} className="print:w-full print:max-w-full print:m-0 print:p-0">
      {/* Print Header - Company Info - Top of Page */}
      {selectedLedgerParty && (
        <div className="hidden print:block border-b-2 border-gray-400 pb-3 mb-4 bg-white p-4">
          <div className="flex items-start justify-between mb-2 gap-4">
            {/* Company Name - Top Left */}
            <div className="text-left flex-shrink-0">
              <h1 
                className={`text-xl font-bold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                style={{
                  ...(companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}),
                  color: '#1e40af',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                }}
              >
                {company?.shopName || company?.username || company?.shopNameUrdu || 'Company Name'}
              </h1>
              {company?.username && (
                <p className={`text-sm text-gray-600 font-medium mt-1 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                  {companyLanguage === 'ur' ? 'نام:' : 'Name:'} {company.username}
                </p>
              )}
              {(company?.contactNumber || company?.phone || company?.mobile) && (
                <p className="text-sm text-gray-700 font-semibold mt-1">
                  {companyLanguage === 'ur' ? 'فون:' : 'Phone:'} {company.contactNumber || company.phone || company.mobile}
                </p>
              )}
            </div>
            
            {/* Logo - Top Right */}
            <div className="flex-shrink-0" style={{ alignSelf: 'flex-start' }}>
              {company?.logo && (
                <img
                  src={company.logo}
                  alt="Logo"
                  className="h-16 w-16 object-contain"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
            </div>
          </div>
          {company?.address && (
            <div className="mt-2 pt-2 border-t border-gray-300">
              <p className={`text-xs text-gray-700 text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
                {company.address}
              </p>
            </div>
          )}
          {/* Customer Khata Title */}
          <div className="mt-3 pt-2">
            <h2 className={`text-lg font-bold text-center ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', serif" } : {}}>
              {companyLanguage === 'ur' ? 'گاہک کھاتا' : 'Customer Khata'}
            </h2>
          </div>
        </div>
      )}

      {/* Customer Khata Tab */}
      <div className="grid md:grid-cols-4 gap-6">
        {/* Parties list */}
        <div className="md:col-span-1 bg-white rounded-lg shadow-lg p-4 border border-brand-card flex flex-col print:hidden">
          <div className={`flex items-center justify-between mb-4 ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
            <h3 className={`text-lg font-semibold text-brand-header ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {companyLanguage === 'ur' ? 'گاہک' : 'Customers'}
            </h3>
          </div>

          {/* Search Filter */}
          <div className="mb-4">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={customerSearchFilter}
                onChange={(e) => setCustomerSearchFilter(e.target.value)}
                onClick={handleSearchInputClick}
                className={`w-full px-4 pl-10 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header transition-all ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}
                style={companyLanguage === 'ur' ? { 
                  fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif",
                  lineHeight: '1.8',
                  paddingTop: '12px',
                  paddingBottom: '12px',
                  fontSize: '13px',
                  height: 'auto',
                  minHeight: '44px',
                  display: 'block',
                  overflow: 'visible',
                  verticalAlign: 'middle'
                } : {}}
                dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                lang={companyLanguage === 'ur' ? 'ur' : 'en'}
                placeholder={companyLanguage === 'ur' ? 'گاہک تلاش کریں...' : 'Search customer...'}
              />
              <svg className={`absolute ${companyLanguage === 'ur' ? 'right-3' : 'left-3'} top-3 w-5 h-5 text-gray-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Add Customer Button */}
          <button
            onClick={() => {
              setEditingPartyId(null);
              setLedgerPartyForm({
                name: '',
                nameUrdu: '',
                type: 'customer',
                phone: '',
                address: '',
                openingBalance: '',
                balanceType: 'receivable'
              });
              setShowCustomerForm(true);
            }}
            className="mb-4 w-full bg-brand-header hover:bg-emerald-800 text-white font-semibold py-3 rounded-lg transition-all transform hover:scale-105 flex items-center justify-center gap-2 shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className={companyLanguage === 'ur' ? 'font-urdu' : ''} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
              {companyLanguage === 'ur' ? 'گاہک شامل کریں' : 'Add Customer'}
            </span>
          </button>

          {/* Create / Edit Party Form - Hidden by default */}
          {showCustomerForm && (
            <form ref={formRef} onSubmit={handleCreateLedgerParty} className="mb-4 space-y-3 p-4 bg-brand-background rounded-lg border border-brand-card">
              <div className={`flex items-center justify-between mb-2 ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
                <h4 className={`text-sm font-semibold text-brand-header ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                  {editingPartyId ? (companyLanguage === 'ur' ? 'پارٹی تبدیل کریں' : 'Edit Party') : (companyLanguage === 'ur' ? 'نئی پارٹی بنائیں' : 'Create New Party')}
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomerForm(false);
                    setEditingPartyId(null);
                    setLedgerPartyForm({
                      name: '',
                      nameUrdu: '',
                      type: 'customer',
                      phone: '',
                      address: '',
                      openingBalance: '',
                      balanceType: 'receivable'
                    });
                  }}
                  className="text-gray-500 hover:text-red-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <input
                    type="text"
                    name="name"
                    value={ledgerPartyForm.name}
                    onChange={handleLedgerPartyFormChange}
                    required
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm"
                    style={{ lineHeight: '1.7', paddingTop: '8px', paddingBottom: '8px', display: 'block', overflow: 'visible' }}
                    placeholder={companyLanguage === 'ur' ? 'نام (انگریزی)' : 'Name (English)'}
                  />
                </div>
                <div className="col-span-2">
                  <input
                    ref={urduNameInputRef}
                    type="text"
                    name="nameUrdu"
                    value={ledgerPartyForm.nameUrdu}
                    onChange={handleLedgerPartyFormChange}
                    onFocus={() => setShowUrduKeyboard(true)}
                    className={`w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm text-right ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif", lineHeight: '1.7', paddingTop: '8px', paddingBottom: '8px', display: 'block', overflow: 'visible' } : { lineHeight: '1.7', paddingTop: '8px', paddingBottom: '8px', display: 'block', overflow: 'visible' }}
                    dir="rtl"
                    lang="ur"
                    inputMode="text"
                    autoComplete="off"
                    placeholder={companyLanguage === 'ur' ? 'نام (اردو)' : 'Name (Urdu)'}
                  />
                </div>
                <div>
                  <input
                    type="hidden"
                    name="type"
                    value="customer"
                  />
                  <div className="w-full px-3 py-1.5 border-2 border-brand-card rounded-lg text-sm bg-brand-background text-gray-600">
                    {companyLanguage === 'ur' ? 'گاہک' : 'Customer'}
                  </div>
                </div>
                <div>
                  <input
                    type="number"
                    name="openingBalance"
                    value={ledgerPartyForm.openingBalance}
                    onChange={handleLedgerPartyFormChange}
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm text-right"
                    placeholder={companyLanguage === 'ur' ? 'موجودہ بیلنس' : 'Current Balance'}
                  />
                </div>
                <div>
                  <select
                    name="balanceType"
                    value={ledgerPartyForm.balanceType}
                    onChange={handleLedgerPartyFormChange}
                    className={`w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm ${companyLanguage === 'ur' ? 'font-urdu' : ''}`}
                    style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                  >
                    <option value="receivable">{companyLanguage === 'ur' ? 'قابل وصول' : 'Receivable'}</option>
                    <option value="payable">{companyLanguage === 'ur' ? 'قابل ادائیگی' : 'Payable'}</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    name="phone"
                    value={ledgerPartyForm.phone}
                    onChange={handleLedgerPartyFormChange}
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm"
                    placeholder={companyLanguage === 'ur' ? 'فون' : 'Phone'}
                  />
                </div>
                <div className="col-span-2">
                  <textarea
                    name="address"
                    value={ledgerPartyForm.address}
                    onChange={handleLedgerPartyFormChange}
                    rows="2"
                    className={`w-full px-3 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-header focus:border-brand-header text-sm ${companyLanguage === 'ur' ? 'font-urdu text-right' : ''}`}
                    style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
                    dir={companyLanguage === 'ur' ? 'rtl' : 'ltr'}
                    placeholder={companyLanguage === 'ur' ? 'پتہ' : 'Address'}
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomerForm(false);
                    setEditingPartyId(null);
                    setLedgerPartyForm({
                      name: '',
                      nameUrdu: '',
                      type: 'customer',
                      phone: '',
                      address: '',
                      openingBalance: '',
                      balanceType: 'receivable'
                    });
                  }}
                  className="flex-1 px-3 py-1.5 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-semibold transition"
                >
                  {companyLanguage === 'ur' ? 'منسوخ' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-brand-header hover:bg-emerald-800 text-white text-sm font-semibold py-1.5 rounded-lg transition-all transform hover:scale-105"
                >
                  {editingPartyId ? (companyLanguage === 'ur' ? 'اپ ڈیٹ کریں' : 'Update Party') : (companyLanguage === 'ur' ? 'محفوظ کریں' : 'Save Party')}
                </button>
              </div>
            </form>
          )}

          {/* Customer List */}
          <div className="flex-1 overflow-y-auto">
            {isLoadingLedger ? (
              <p className={`text-gray-500 text-sm text-center py-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                {companyLanguage === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...'}
              </p>
            ) : (() => {
              const filteredParties = ledgerParties.filter(party => {
                // Filter by customer type first
                if (party.type !== 'customer') return false;
                if (!customerSearchFilter) return true;
                
                const searchLower = customerSearchFilter.toLowerCase();
                return (
                  party.name?.toLowerCase().includes(searchLower) ||
                  party.nameUrdu?.toLowerCase().includes(searchLower) ||
                  party.nameUrdu?.includes(customerSearchFilter) || // Also check without lowercase for exact Urdu match
                  party.phone?.includes(customerSearchFilter)
                );
              });

              if (filteredParties.length === 0) {
                return (
                  <p className={`text-gray-500 text-sm text-center py-4 ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {customerSearchFilter ? (companyLanguage === 'ur' ? 'کوئی گاہک نہیں ملا' : 'No customer found') : (companyLanguage === 'ur' ? 'ابھی کوئی پارٹی نہیں' : 'No parties yet')}
                  </p>
                );
              }

              return (
                <ul className="divide-y divide-gray-200">
                  {filteredParties.map((party) => (
                    <li
                      key={party._id}
                      onClick={() => fetchLedgerTransactions(party._id)}
                      className={`py-2 px-2 cursor-pointer rounded transition ${
                        selectedLedgerParty && selectedLedgerParty._id === party._id
                          ? 'bg-brand-header text-white'
                          : 'hover:bg-brand-background'
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className={`font-semibold ${companyLanguage === 'ur' ? 'font-urdu' : ''}`} style={companyLanguage === 'ur' ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                          {companyLanguage === 'ur' ? (party.nameUrdu || party.name) : party.name}
                        </span>
                        <span className="text-xs opacity-70">
                          {party.type === 'customer'
                            ? companyLanguage === 'ur' ? 'گاہک' : 'Customer'
                            : companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                        </span>
                      </div>
                      <p className={`text-xs mt-1 ${selectedLedgerParty && selectedLedgerParty._id === party._id ? 'text-white/80' : 'text-gray-500'}`}>
                        {companyLanguage === 'ur' ? 'بیلنس:' : 'Balance:'}{' '}
                        <span className={`font-semibold ${
                          (party.balanceType === 'payable' || (party.currentBalance < 0)) 
                            ? 'text-red-600' 
                            : ''
                        }`}>
                          {party.balanceType === 'payable' || (party.currentBalance < 0)
                            ? `-${Math.abs(party.currentBalance || 0).toLocaleString()}` 
                            : (party.currentBalance?.toLocaleString() || 0)}
                        </span>
                        {parseFloat(party.currentBalance || 0) !== 0 && (
                          <span className="text-xs opacity-70 ml-1">
                            {party.balanceType === 'receivable' ? (companyLanguage === 'ur' ? 'قابل وصول' : 'Receivable') : (companyLanguage === 'ur' ? 'قابل ادائیگی' : 'Payable')}
                          </span>
                        )}
                      </p>
                    </li>
                  ))}
                </ul>
              );
            })()}
          </div>
        </div>

        {/* Transactions */}
        <div className="md:col-span-3 bg-white rounded-lg shadow-lg p-4 border border-brand-card print:col-span-full">
          {selectedLedgerParty ? (
            <>
              <div className={`flex justify-between items-center mb-4 ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {companyLanguage === 'ur'
                      ? (selectedLedgerParty.nameUrdu || selectedLedgerParty.name)
                      : selectedLedgerParty.name}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">
                    {companyLanguage === 'ur' ? 'موجودہ بیلنس:' : 'Current Balance:'}{' '}
                    <span className={`font-semibold ${
                      (selectedLedgerParty.balanceType === 'payable' || (selectedLedgerParty.currentBalance < 0)) 
                        ? 'text-red-600' 
                        : ''
                    }`}>
                      {selectedLedgerParty.balanceType === 'payable' || (selectedLedgerParty.currentBalance < 0)
                        ? `-${Math.abs(selectedLedgerParty.currentBalance || 0).toLocaleString()}`
                        : (selectedLedgerParty.currentBalance?.toLocaleString() || 0)}
                    </span>
                  </p>
                </div>
              </div>

              {/* Date Filter and Action Buttons */}
              <div className={`mb-4 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between print:hidden ${companyLanguage === 'ur' ? 'flex-row-reverse' : ''}`}>
                <div className="flex flex-col md:flex-row gap-2">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                    placeholder={companyLanguage === 'ur' ? 'تاریخ سے' : 'From Date'}
                  />
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded text-sm"
                    placeholder={companyLanguage === 'ur' ? 'تاریخ تک' : 'To Date'}
                  />
                  {(dateFrom || dateTo) && (
                    <button
                      onClick={() => {
                        setDateFrom('');
                        setDateTo('');
                      }}
                      className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50"
                    >
                      {companyLanguage === 'ur' ? 'کلیر' : 'Clear'}
                    </button>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleDownloadPDF}
                    disabled={isGeneratingPDF}
                    className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {isGeneratingPDF ? (companyLanguage === 'ur' ? 'لوڈ ہو رہا ہے...' : 'Loading...') : (companyLanguage === 'ur' ? 'PDF ڈاؤن لوڈ' : 'Download PDF')}
                  </button>
                  <button
                    onClick={handleDownloadExcel}
                    className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {companyLanguage === 'ur' ? 'Excel ڈاؤن لوڈ' : 'Download Excel'}
                  </button>
                  <button
                    onClick={handlePrint}
                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-all flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    {companyLanguage === 'ur' ? 'پرنٹ' : 'Print'}
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                {(() => {
                  // Get sales for selected customer
                  const customerSales = selectedLedgerParty 
                    ? sales.filter(sale => sale.customerPartyId === selectedLedgerParty._id)
                    : [];
                  
                  // Get sale IDs that already have ledger transactions
                  const salesWithLedgerTransactions = new Set();
                  ledgerTransactions.forEach(tx => {
                    const saleId = tx.saleId?._id || (tx.saleId && typeof tx.saleId === 'object' ? tx.saleId._id : tx.saleId) || tx.saleId;
                    if (saleId) {
                      salesWithLedgerTransactions.add(saleId.toString());
                    }
                  });
                  
                  // Filter transactions by date for totals
                  const filteredLedgerTransactions = filterTransactionsByDate(ledgerTransactions);
                  
                  // Calculate totals from filtered ledger transactions
                  let totalDebit = filteredLedgerTransactions.reduce(
                    (sum, tx) => (tx.type === 'debit' ? sum + (tx.amount || 0) : sum),
                    0
                  );
                  let totalCredit = filteredLedgerTransactions.reduce(
                    (sum, tx) => (tx.type === 'credit' ? sum + (tx.amount || 0) : sum),
                    0
                  );
                  
                  // Add sales totals (only for sales without ledger transactions to avoid double-counting)
                  // Also filter sales by date
                  customerSales.forEach(sale => {
                    if (!salesWithLedgerTransactions.has(sale._id.toString())) {
                      const saleDate = new Date(sale.date || sale.createdAt || 0);
                      const fromDate = dateFrom ? new Date(dateFrom) : null;
                      const toDate = dateTo ? new Date(dateTo) : null;
                      
                      let includeSale = true;
                      if (fromDate && toDate) {
                        fromDate.setHours(0, 0, 0, 0);
                        toDate.setHours(23, 59, 59, 999);
                        includeSale = saleDate >= fromDate && saleDate <= toDate;
                      } else if (fromDate) {
                        fromDate.setHours(0, 0, 0, 0);
                        includeSale = saleDate >= fromDate;
                      } else if (toDate) {
                        toDate.setHours(23, 59, 59, 999);
                        includeSale = saleDate <= toDate;
                      }
                      
                      if (includeSale) {
                        const saleAmount = parseFloat(sale.totalAmount || 0);
                        const amountPaid = parseFloat(sale.amountPaid || 0);
                        totalDebit += saleAmount;
                        totalCredit += amountPaid;
                      }
                    }
                  });
                  
                  return (
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-brand-background">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {companyLanguage === 'ur' ? 'تاریخ' : 'Date'}
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {companyLanguage === 'ur' ? 'تفصیل' : 'Particulars'}
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {companyLanguage === 'ur' ? 'سپلائر' : 'Supplier'}
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {companyLanguage === 'ur' ? 'گاڑی نمبر' : 'Gardi No.'}
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {companyLanguage === 'ur' ? 'واؤچر نمبر' : 'Vch No.'}
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {companyLanguage === 'ur' ? 'سیل' : 'Sale'}
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {companyLanguage === 'ur' ? 'ریکوری' : 'Recovery'}
                          </th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {companyLanguage === 'ur' ? 'بیلنس' : 'Balance'}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {/* Opening Balance row */}
                        <tr>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                            {/* empty date */}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700 font-semibold">
                            {companyLanguage === 'ur' ? 'ابتدائی بیلنس' : 'Opening Balance'}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">-</td>
                          <td className="px-3 py-2 whitespace-nowrap text-gray-700">-</td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">-</td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">-</td>
                          <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">-</td>
                          <td className={`px-3 py-2 whitespace-nowrap text-right font-semibold ${
                            (selectedLedgerParty.balanceType === 'payable' || (selectedLedgerParty.openingBalance < 0))
                              ? 'text-red-600'
                              : 'text-gray-900'
                          }`}>
                            {selectedLedgerParty.balanceType === 'payable' || (selectedLedgerParty.openingBalance < 0)
                              ? `-${Math.abs(selectedLedgerParty.openingBalance || 0).toLocaleString()}`
                              : (selectedLedgerParty.openingBalance || 0).toLocaleString()}
                          </td>
                        </tr>

                        {(() => {
                          // Get sales for selected customer (same as roz namacha)
                          const customerSales = selectedLedgerParty 
                            ? sales.filter(sale => sale.customerPartyId === selectedLedgerParty._id)
                            : [];
                          
                          // Get sale IDs that already have ledger transactions
                          const salesWithLedgerTransactions = new Set();
                          ledgerTransactions.forEach(tx => {
                            const saleId = tx.saleId?._id || (tx.saleId && typeof tx.saleId === 'object' ? tx.saleId._id : tx.saleId) || tx.saleId;
                            if (saleId) {
                              salesWithLedgerTransactions.add(saleId.toString());
                            }
                          });
                          
                          // Convert sales to transaction format (only for sales without ledger transactions)
                          // Create combined transaction for sale (will be merged in grouping logic)
                          const saleTransactions = customerSales
                            .filter(sale => !salesWithLedgerTransactions.has(sale._id.toString()))
                            .map(sale => {
                              const saleAmount = parseFloat(sale.totalAmount || 0);
                              const amountPaid = parseFloat(sale.amountPaid || 0);
                              const saleSerial = sale.serialNumber || sale._id?.toString().slice(-6) || '';
                              
                              return {
                                _id: `sale_${sale._id}`,
                                date: sale.date || sale.createdAt,
                                description: `سیل #${saleSerial}`,
                                voucherNumber: saleSerial,
                                sale: saleAmount,
                                recovery: amountPaid,
                                balanceAfter: null, // Will be calculated
                                saleId: sale._id,
                                billId: null,
                                isFromSale: true,
                                saleData: sale // Keep original sale data
                              };
                            });
                          
                          // Combine ledger transactions and sale transactions (without duplicates)
                          let allTransactions = [...ledgerTransactions, ...saleTransactions];
                          
                          // Apply date filter
                          allTransactions = filterTransactionsByDate(allTransactions);
                          
                          if (allTransactions.length === 0) {
                            return (
                              <tr>
                                <td colSpan={8} className="px-3 py-4 text-center text-gray-500">
                                  {companyLanguage === 'ur' ? 'کوئی ٹرانزیکشن نہیں' : 'No transactions yet'}
                                </td>
                              </tr>
                            );
                          }
                          
                          // Group sale receipt transactions (debit + credit) together
                          // But keep separate recovery transactions separate
                          const groupedBySale = {};
                          const separateTransactions = [];
                          
                          allTransactions.forEach(tx => {
                            // Handle saleId - can be object (populated) or string (ID) or null
                            let saleIdValue = null;
                            if (tx.saleId) {
                              if (typeof tx.saleId === 'object' && tx.saleId._id) {
                                saleIdValue = tx.saleId._id.toString();
                              } else if (typeof tx.saleId === 'object') {
                                saleIdValue = tx.saleId.toString();
                              } else {
                                saleIdValue = tx.saleId.toString();
                              }
                            }
                            
                            const billIdValue = tx.billId?._id || (tx.billId && typeof tx.billId === 'object' ? tx.billId._id : tx.billId) || tx.billId;
                            
                            // If transaction has saleId, group it with other transactions from same sale
                            // But only if it's a sale-related transaction (debit or credit from sale)
                            if (saleIdValue && (tx.type === 'debit' || tx.type === 'credit' || tx.isFromSale)) {
                              if (!groupedBySale[saleIdValue]) {
                                groupedBySale[saleIdValue] = {
                                  transactions: [],
                                  saleId: saleIdValue
                                };
                              }
                              groupedBySale[saleIdValue].transactions.push(tx);
                            } else {
                              // Separate transaction (recovery without saleId, or manual transaction)
                              separateTransactions.push(tx);
                            }
                          });
                          
                          // Convert grouped sale transactions to display format
                          const displayTransactions = [];
                          
                          // Process grouped sale transactions (merge debit + credit)
                          Object.values(groupedBySale).forEach(group => {
                            const saleTxs = group.transactions;
                            if (saleTxs.length === 0) return;
                            
                            // Get saleId value
                            const saleIdValue = group.saleId;
                            
                            // Get voucher number from first transaction
                            let voucherNumber = '-';
                            let description = '';
                            let date = saleTxs[0].date;
                            let balanceAfter = 0;
                            
                            // Find debit and credit amounts
                            let saleAmount = 0;
                            let recoveryAmount = 0;
                            // Avoid double-counting if both synthetic (from Sale model) and real ledger tx exist
                            const hasRealDebit = saleTxs.some(t => !t.isFromSale && t.type === 'debit');
                            const hasRealCredit = saleTxs.some(t => !t.isFromSale && t.type === 'credit');
                            
                            saleTxs.forEach(tx => {
                              // Get voucher number
                              if (tx.isFromSale && tx.saleData) {
                                voucherNumber = tx.saleData.serialNumber || tx.saleData._id?.toString().slice(-6) || 'S-XXXXXX';
                                description = `سیل #${voucherNumber}`;
                              } else if (tx.saleId) {
                                const saleSerial = (tx.saleId && typeof tx.saleId === 'object' && tx.saleId.serialNumber) 
                                                  ? tx.saleId.serialNumber 
                                                  : null;
                                
                                if (!saleSerial && tx.description) {
                                  const match = tx.description.match(/#(\d+)/);
                                  if (match) {
                                    voucherNumber = match[1];
                                    description = `سیل #${voucherNumber}`;
                                  } else {
                                    voucherNumber = saleIdValue ? `S-${saleIdValue.toString().slice(-6)}` : 'S-XXXXXX';
                                    description = `سیل #${voucherNumber}`;
                                  }
                                } else {
                                  voucherNumber = saleSerial || (saleIdValue ? `S-${saleIdValue.toString().slice(-6)}` : 'S-XXXXXX');
                                  description = `سیل #${voucherNumber}`;
                                }
                              }
                              
                              // Sum amounts
                              if (tx.isFromSale) {
                                if (!hasRealDebit) saleAmount += tx.sale || 0;
                                if (!hasRealCredit) recoveryAmount += tx.recovery || 0;
                              } else {
                                if (tx.type === 'debit') {
                                  saleAmount += tx.amount || 0;
                                } else if (tx.type === 'credit') {
                                  recoveryAmount += tx.amount || 0;
                                }
                              }
                              
                              // Use latest balance
                              if (tx.balanceAfter && (!balanceAfter || new Date(tx.date) > new Date(date))) {
                                balanceAfter = tx.balanceAfter;
                                date = tx.date;
                              }
                            });
                            
                            // Clean description
                            description = description.replace(' (سیل)', '').replace(' (ادائیگی)', '').replace(' (Sale)', '').replace(' (Recovery)', '') || `سیل #${voucherNumber}`;
                            
                            displayTransactions.push({
                              date: date,
                              description: description,
                              voucherNumber: voucherNumber,
                              sale: saleAmount,
                              recovery: recoveryAmount,
                              balanceAfter: balanceAfter,
                              billId: null,
                              saleId: saleIdValue,
                              _id: `sale_group_${saleIdValue}`,
                              isFromSale: true,
                              transactionType: 'sale_receipt'
                            });
                          });
                          
                          // Process separate transactions (recoveries, manual transactions, etc.)
                          separateTransactions.forEach(tx => {
                            const billIdValue = tx.billId?._id || (tx.billId && typeof tx.billId === 'object' ? tx.billId._id : tx.billId) || tx.billId;
                            
                            // Get voucher number
                            let voucherNumber = '-';
                            if (billIdValue) {
                              const billSerial = (tx.billId && typeof tx.billId === 'object' && tx.billId.serialNumber) 
                                                ? tx.billId.serialNumber 
                                                : null;
                              voucherNumber = billSerial || (billIdValue ? billIdValue.toString().slice(-6) : '-');
                            } else if (tx.description) {
                              // Try to extract from description for recovery receipts
                              const match = tx.description.match(/#(\d+)/);
                              if (match) {
                                voucherNumber = match[1];
                              }
                            }
                            
                            // Clean description
                            let description = tx.description || '-';
                            description = description.replace(' (سیل)', '').replace(' (ادائیگی)', '').replace(' (ریکوری)', '').replace(' (Sale)', '').replace(' (Recovery)', '') || '-';
                            
                            // Determine amounts
                            let saleAmount = 0;
                            let recoveryAmount = 0;
                            
                            if (tx.type === 'debit') {
                              saleAmount = tx.amount || 0;
                            } else if (tx.type === 'credit') {
                              recoveryAmount = tx.amount || 0;
                            }
                            
                            displayTransactions.push({
                              date: tx.date,
                              description: description,
                              voucherNumber: voucherNumber,
                              sale: saleAmount,
                              recovery: recoveryAmount,
                              balanceAfter: tx.balanceAfter || 0,
                              billId: billIdValue,
                              saleId: null,
                              _id: tx._id || `tx_${Date.now()}_${Math.random()}`,
                              isFromSale: false,
                              transactionType: tx.type || 'ledger'
                            });
                          });
                          
                          // Sort transactions by date
                          const sortedTransactions = displayTransactions.sort((a, b) => {
                            const dateA = new Date(a.date || 0);
                            const dateB = new Date(b.date || 0);
                            return dateA - dateB;
                          });
                          
                          // Calculate running balance: Sale adds to balance, Recovery subtracts from balance
                          let runningBalance = selectedLedgerParty.openingBalance || 0;
                          
                          // Adjust opening balance based on balanceType
                          if (selectedLedgerParty.balanceType === 'receivable') {
                            // Receivable means customer owes us, so positive balance
                            runningBalance = Math.abs(selectedLedgerParty.openingBalance || 0);
                          } else if (selectedLedgerParty.balanceType === 'payable') {
                            // Payable means we owe customer, so negative balance
                            runningBalance = -Math.abs(selectedLedgerParty.openingBalance || 0);
                          }
                          
                          return sortedTransactions.map((tx, txIndex) => {
                            // Calculate balance after this transaction
                            // Sale adds to balance, Recovery subtracts from balance
                            runningBalance = runningBalance + (tx.sale || 0) - (tx.recovery || 0);
                            
                            // Update tx.balanceAfter with calculated running balance
                            tx.balanceAfter = runningBalance;
                            
                            return (
                            <tr key={tx._id}>
                              <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                {new Date(tx.date).toLocaleDateString()}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                {tx.description}
                                {tx.saleId && (
                                  <span className="ml-2 text-xs text-blue-600">
                                    {companyLanguage === 'ur' ? '(سیل)' : '(Sale)'}
                                  </span>
                                )}
                                {tx.billId && (
                                  <span className="ml-2 text-xs text-purple-600">
                                    {companyLanguage === 'ur' ? '(بل)' : '(Bill)'}
                                  </span>
                                )}
                                {!tx.saleId && !tx.billId && tx.recovery > 0 && (
                                  <span className="ml-2 text-xs text-green-600">
                                    {companyLanguage === 'ur' ? '(ریکوری)' : '(Recovery)'}
                                  </span>
                                )}
                              </td>
                              {(() => {
                                const { supplierName, gardiNumber } = getSupplierAndVehicleForTx(tx);
                                return (
                                  <>
                                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                      {supplierName}
                                    </td>
                                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                                      {gardiNumber}
                                    </td>
                                  </>
                                );
                              })()}
                              <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">
                                {tx.voucherNumber}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">
                                {tx.sale > 0 ? tx.sale.toLocaleString() : ''}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-right text-gray-700">
                                {tx.recovery > 0 ? tx.recovery.toLocaleString() : ''}
                              </td>
                              <td className={`px-3 py-2 whitespace-nowrap text-right font-semibold ${
                                (selectedLedgerParty.balanceType === 'payable' || (tx.balanceAfter < 0))
                                  ? 'text-red-600'
                                  : 'text-gray-900'
                              }`}>
                                {selectedLedgerParty.balanceType === 'payable' || (tx.balanceAfter < 0)
                                  ? `-${Math.abs(tx.balanceAfter || 0).toLocaleString()}`
                                  : (tx.balanceAfter?.toLocaleString() || '')}
                              </td>
                            </tr>
                            );
                          });
                        })()}

                        {/* Closing balance summary */}
                        <tr className="bg-brand-background">
                          <td className="px-3 py-2 text-gray-700 font-semibold" colSpan={5}>
                            {companyLanguage === 'ur' ? 'اختتامی بیلنس' : 'Closing Balance'}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900 font-semibold">
                            {totalDebit.toLocaleString()}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900 font-semibold">
                            {totalCredit.toLocaleString()}
                          </td>
                          <td className={`px-3 py-2 text-right font-semibold ${
                            (selectedLedgerParty.balanceType === 'payable' || (selectedLedgerParty.currentBalance < 0))
                              ? 'text-red-600'
                              : 'text-gray-900'
                          }`}>
                            {selectedLedgerParty.balanceType === 'payable' || (selectedLedgerParty.currentBalance < 0)
                              ? `-${Math.abs(selectedLedgerParty.currentBalance || 0).toLocaleString()}`
                              : (selectedLedgerParty.currentBalance || 0).toLocaleString()}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </>
          ) : (
            <p className="text-gray-500 text-sm">
              {companyLanguage === 'ur' ? 'کوئی پارٹی منتخب نہیں' : 'No party selected'}
            </p>
          )}
        </div>
      </div>
      </div>
      
      {/* Print Styles */}
      <style>{`
        @media print {
          .print\\:hidden {
            display: none !important;
          }
          .print\\:col-span-full {
            grid-column: 1 / -1 !important;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          @page {
            margin: 0.5cm;
          }
          .grid {
            display: block !important;
          }
          .md\\:grid-cols-4 {
            display: block !important;
          }
          .bg-white {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0.5cm !important;
          }
          .rounded-lg {
            border-radius: 0 !important;
          }
          .shadow-lg {
            box-shadow: none !important;
          }
          table {
            width: 100% !important;
            max-width: 100% !important;
          }
          .overflow-x-auto {
            width: 100% !important;
            overflow: visible !important;
          }
          [ref] {
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
        }
      `}</style>
      <UrduKeyboard
        isVisible={showUrduKeyboard}
        onKeyPress={handleUrduKeyPress}
        onClose={() => setShowUrduKeyboard(false)}
      />
      <UrduKeyboard
        isVisible={showSearchUrduKeyboard}
        onKeyPress={handleSearchUrduKeyPress}
        onClose={() => setShowSearchUrduKeyboard(false)}
      />
    </div>
  );
}

export default CustomerKhata;

